import type { DraggableAttributes } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { Box, Menu, type MenuProps, Tooltip } from '@mantine/core'
import { IconArrowDown, IconArrowUp, IconCopy, IconGripVertical, IconTrash } from '@tabler/icons-react'
import { forwardRef, useEffect, useState } from 'react'

const dragTooltipStyles = {
  tooltip: {
    textAlign: 'center' as const,
    '& em': {
      fontStyle: 'normal',
      color: '#868e96',
      display: 'inline-block',
    },
  },
}

interface EntryMenuButtonProps extends Omit<MenuProps, 'children'> {
  onMoveUp?: () => void
  onMoveDown?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  canDelete?: boolean
  canDuplicate?: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
  isDragging: boolean
  attributes?: DraggableAttributes
  listeners?: SyntheticListenerMap
}

export const EntryMenuButton = forwardRef<HTMLDivElement, EntryMenuButtonProps>(
  (
    {
      onMoveUp,
      onMoveDown,
      onDuplicate,
      onDelete,
      canDelete = true,
      canDuplicate = true,
      canMoveUp = true,
      canMoveDown = true,
      isDragging,
      attributes,
      listeners,
      ...props
    },
    ref,
  ) => {
    const [dragTooltipOpened, setDragTooltipOpened] = useState(false)
    const [menuOpened, setMenuOpened] = useState(false)

    useEffect(() => {
      if (isDragging || menuOpened) {
        setDragTooltipOpened(false)
      }
    }, [isDragging, menuOpened])

    return (
      <Menu opened={menuOpened} onChange={setMenuOpened} {...props}>
        <Menu.Target>
          <Tooltip
            label={
              <>
                <strong>Drag</strong> to move
                <br />
                <strong>Click</strong> to open menu
              </>
            }
            position="top"
            multiline
            styles={dragTooltipStyles}
            transitionProps={{ duration: 100 }}
            opened={dragTooltipOpened && !menuOpened && !isDragging}>
            <div>
              <Box
                ref={ref}
                {...attributes}
                {...listeners}
                className="drag-handle"
                style={{
                  cursor: 'grab',
                  opacity: 0.2,
                  transition: 'all 200ms ease',
                  borderRadius: 4,
                  padding: '4px 6px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => setMenuOpened(!menuOpened)}
                onMouseOut={() => setDragTooltipOpened(false)}
                // onMouseEnter={() => setDragTooltipOpened(true)}
                onMouseOver={() => setDragTooltipOpened(true)}
                // onMouseLeave={() => setDragTooltipOpened(false)}
                onPointerLeave={() => setDragTooltipOpened(false)}>
                <IconGripVertical size={18} />
              </Box>
            </div>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconArrowUp size={14} />} onClick={onMoveUp} disabled={!canMoveUp}>
            Move Up
          </Menu.Item>
          <Menu.Item leftSection={<IconArrowDown size={14} />} onClick={onMoveDown} disabled={!canMoveDown}>
            Move Down
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item leftSection={<IconCopy size={14} />} onClick={onDuplicate} disabled={!canDuplicate}>
            Duplicate
          </Menu.Item>
          <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={onDelete} disabled={!canDelete}>
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    )
  },
)

EntryMenuButton.displayName = 'EntryMenuButton'
